import type { TableKey } from '@/constants/table-columns-config';

export const SETTINGS_COOKIES = {
  themeMode: 'settings-theme-mode',
  themeContrast: 'settings-theme-contrast',
  themeLayout: 'settings-theme-layout',
  themeColorPresets: 'settings-theme-color-presets',
  themeStretch: 'settings-theme-stretch',
} as const;

const THEME_MODES = ['light', 'dark'] as const;
const THEME_CONTRASTS = ['default', 'bold'] as const;
const THEME_LAYOUTS = ['vertical', 'horizontal', 'mini'] as const;
const THEME_COLOR_PRESETS = ['cyan', 'purple', 'blue', 'orange', 'red'] as const;

export type ThemeMode = (typeof THEME_MODES)[number];
export type ThemeContrast = (typeof THEME_CONTRASTS)[number];
export type ThemeLayout = (typeof THEME_LAYOUTS)[number];
export type ThemeColorPresets = (typeof THEME_COLOR_PRESETS)[number];

export const isThemeMode = (value: unknown): value is ThemeMode =>
  THEME_MODES.includes(value as ThemeMode);

export const isThemeContrast = (value: unknown): value is ThemeContrast =>
  THEME_CONTRASTS.includes(value as ThemeContrast);

export const isThemeLayout = (value: unknown): value is ThemeLayout =>
  THEME_LAYOUTS.includes(value as ThemeLayout);

export const isThemeColorPresets = (value: unknown): value is ThemeColorPresets =>
  THEME_COLOR_PRESETS.includes(value as ThemeColorPresets);

export const parseThemeStretch = (value: unknown): boolean => value === 'true' || value === true;

export type SettingsValueProps = {
  themeStretch: boolean;
  themeMode: ThemeMode;
  themeContrast: ThemeContrast;
  themeLayout: ThemeLayout;
  themeColorPresets: ThemeColorPresets;
};

export const DEFAULT_THEME_SETTINGS: SettingsValueProps = {
  themeMode: 'light',
  themeContrast: 'default',
  themeLayout: 'vertical',
  themeColorPresets: 'cyan',
  themeStretch: false,
};

export type SettingsCookies = SettingsValueProps & {
  tableInitialWidths: Record<TableKey, Record<string, number>>;
};

export const DEFAULT_SETTINGS: SettingsCookies = {
  ...DEFAULT_THEME_SETTINGS,
  tableInitialWidths: {} as Record<TableKey, Record<string, number>>,
};

export type SettingsContextProps = SettingsValueProps & {
  tableInitialWidths: Record<TableKey, Record<string, number>>;
  // Update
  onUpdate: <K extends keyof SettingsValueProps>(name: K, value: SettingsValueProps[K]) => void;
  // Reset
  canReset: boolean;
  onReset: VoidFunction;
  // Drawer
  open: boolean;
  onToggle: VoidFunction;
  onClose: VoidFunction;
};
